import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  Grid,
  CircularProgress,
  Badge,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Chip,
  Collapse,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Send as SendIcon,
  Refresh as RefreshIcon,
  Chat as ChatIcon,
  Info as InfoIcon,
  WifiOff as WifiOffIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useThemeContext } from '../../context/ThemeContext';
import chatService, { Message } from '../../api/chatService';
import websocketService from '../../api/websocketService';
import ChatMessage from './ChatMessage';
import DateSeparator from './DateSeparator';
import TypingIndicator from './TypingIndicator';
import ChatNotificationSettings from './ChatNotificationSettings';
import { blue, red, green } from '@mui/material/colors';

const ChatPage: React.FC = () => {
  const { user } = useAuth();
  const { mode } = useThemeContext();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showTyping, setShowTyping] = useState(false); // For typing indicator
  const [wsConnected, setWsConnected] = useState(false); // WebSocket connection status
  const [showSettings, setShowSettings] = useState(false); // For notification settings
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  
  // Theme and responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Initial fetch on mount
  useEffect(() => {
    // Initial fetch only, no periodic updates
    console.log('Initial message load');
    fetchMessages();
  }, []);
  
  // WebSocket setup in a separate effect
  useEffect(() => {
    // WebSocket connection handler
    const handleConnectionStatus = (connected: boolean) => {
      console.log('WebSocket connection status changed to:', connected);
      setWsConnected(connected);
      
      // If we reconnect, immediately fetch messages to ensure we're up to date
      if (connected) {
        console.log('WebSocket connected, fetching latest messages');
        silentFetchMessages();
      } else {
        // If disconnected, try to reconnect with token
        const token = localStorage.getItem('token');
        if (token) {
          console.log('WebSocket disconnected, attempting to reconnect');
          websocketService.reconnect(token);
        }
      }
    };
    
    // WebSocket message handler with strict duplicate prevention
    const handleNewMessage = (message: Message) => {
      console.log('New message received from WebSocket:', message.id);
      
      // Record that we received a message - important for connection detection
      localStorage.setItem('lastMessageTime', Date.now().toString());

      // Log the full message for debugging
      console.log('DETAILED MESSAGE:', JSON.stringify(message));
      
      // If we're receiving messages, we're definitely connected
      setWsConnected(true);
      
      // Check if message already exists in the list - optimized for performance
      setMessages(prev => {
        // Skip this update if message ID is invalid
        if (!message.id) {
          console.warn('Skipping WebSocket message with no ID');
          return prev;
        }
        
        // Create a map of IDs for faster lookups
        const existingIds = new Set(prev.map(m => m.id));
        
        // Strictly check if message exists by ID
        if (!existingIds.has(message.id)) {
          console.log('Adding new WebSocket message:', message.id);
          
          // Create new array with new message and sort
          const newMessages = [...prev, message].sort((a, b) => 
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          
          // Scroll to bottom with animation frame
          requestAnimationFrame(scrollToBottom);
          return newMessages;
        }
        
        // If message exists and only read status changed
        const existingMessage = prev.find(m => m.id === message.id);
        if (existingMessage && message.is_read !== existingMessage.is_read) {
          console.log('Updating read status for message:', message.id);
          return prev.map(m => m.id === message.id ? message : m);
        }
        
        // If we get here, message is a duplicate so don't update state
        console.log('Ignored duplicate WebSocket message:', message.id);
        return prev;
      });
    };
    
    // Initial connection check
    const token = localStorage.getItem('token');
    if (token) {
      console.log('Ensuring WebSocket is connected');
      websocketService.reconnect(token);
    }
    
    // Register WebSocket handlers
    websocketService.onMessage(handleNewMessage);
    websocketService.onConnection(handleConnectionStatus);
    
    // Set initial status to true - we'll assume connected by default
    // This provides a better UX than showing disconnected initially
    setWsConnected(true);
    
    // Every minute, check if we need to fetch missed messages
    // This is a safety net, shouldn't cause UI refreshes
    const safetyFetchInterval = setInterval(() => {
      console.log('Safety check: fetching any missed messages');
      silentFetchMessages();
    }, 15000); // Every 15 seconds
    
    // Clean up
    return () => {
      websocketService.offMessage(handleNewMessage);
      websocketService.offConnection(handleConnectionStatus);
      clearInterval(safetyFetchInterval);
    };
  }, []);
  
  // Unread count handler in a separate effect to prevent render cascades
  useEffect(() => {
    const handleUnreadCount = (count: number) => {
      // Record that we received a WebSocket event - important for connection detection
      localStorage.setItem('lastMessageTime', Date.now().toString());
      
      // If we're receiving unread counts, we're definitely connected
      setWsConnected(true);
      
      // Only update if the count actually changed
      if (unreadCount !== count) {
        setUnreadCount(count);
      }
    };
    
    websocketService.onUnreadCount(handleUnreadCount);
    
    return () => {
      websocketService.offUnreadCount(handleUnreadCount);
    };
  }, [unreadCount]);
  
  // Handle tab visibility for background tab optimization
  // Also notify server of activity to prevent email notifications when viewing chat
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Silently check for missed messages when tab becomes visible
        silentFetchMessages();
        
        // Signal that the user is actively viewing the chat page
        // This prevents sending email notifications when user is already looking at messages
        websocketService.sendActivity();
      }
    };
    
    // Track user activity to prevent notifications while actively chatting
    const handleActivity = () => {
      // Only send activity events every 30 seconds to avoid flooding
      if (!window.lastActivitySent || Date.now() - window.lastActivitySent > 30000) {
        websocketService.sendActivity();
        window.lastActivitySent = Date.now();
      }
    };
    
    // Send initial activity notification when component mounts
    websocketService.sendActivity();
    window.lastActivitySent = Date.now();
    
    // Listen for user activity events
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('keydown', handleActivity);
    document.addEventListener('click', handleActivity);
    document.addEventListener('scroll', handleActivity);
    
    // Set up regular activity ping while page is open
    const activityInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        websocketService.sendActivity();
        window.lastActivitySent = Date.now();
      }
    }, 60000); // Send activity once per minute while page is open
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('keydown', handleActivity);
      document.removeEventListener('click', handleActivity);
      document.removeEventListener('scroll', handleActivity);
      clearInterval(activityInterval);
    };
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Rather than checking periodically, just react to connection events
  useEffect(() => {
    // Skip any automatic checking - rely solely on explicit connection events
    console.log('Connection status display initialized');
    
    // No intervals or timers
    return () => {};
  }, [wsConnected]);

  // Regular fetch with loading indicators (for initial load or manual refresh)
  const fetchMessages = async () => {
    try {
      setLoading(true);
      // Try to fetch all messages (for admins) or just the user's own messages
      const response = await chatService.getMessages(100, 0, true);
      if (response && response.messages) {
        // First, make sure there are no duplicate IDs in the response itself
        const uniqueMessages = [];
        const seenIds = new Set();
        
        for (const message of response.messages) {
          if (!seenIds.has(message.id)) {
            seenIds.add(message.id);
            uniqueMessages.push(message);
          }
        }
        
        // Sort the messages by timestamp
        const sortedMessages = uniqueMessages.sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        
        // Update the messages state with the unique, sorted messages
        setMessages(sortedMessages);
        
        console.log(`Fetched ${uniqueMessages.length} unique messages`);
        
        // Auto-mark Slack messages as read when viewing the chat
        const unreadMessages = sortedMessages.filter(m => 
          m.is_from_slack && !m.is_read
        );
        
        if (unreadMessages.length > 0) {
          console.log(`Auto-marking ${unreadMessages.length} messages as read`);
          for (const message of unreadMessages) {
            // Don't await these to avoid blocking
            chatService.markAsRead(message.id).catch(err => 
              console.error(`Failed to mark message ${message.id} as read:`, err)
            );
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Silent background fetch without loading indicators
  const silentFetchMessages = async () => {
    try {
      // Don't show loading state
      const response = await chatService.getMessages(100, 0, true);
      if (response && response.messages) {
        // Create a map of existing messages for faster lookups
        const existingMessageIds = new Set(messages.map(msg => msg.id));
        
        // Find truly new messages by checking against our map
        const newMessages = response.messages.filter(newMsg => !existingMessageIds.has(newMsg.id));
        
        // Only update if there are actual new messages
        if (newMessages.length > 0) {
          console.log(`Found ${newMessages.length} new messages during silent check`);
          setMessages(prev => {
            // Start with existing messages
            const existingMessages = [...prev];
            
            // For each new message, check again to avoid race conditions
            // This double-check prevents duplicates in case of concurrent updates
            const uniqueNewMessages = newMessages.filter(newMsg => 
              !existingMessages.some(existingMsg => existingMsg.id === newMsg.id)
            );
            
            // If after double-checking there are still new messages
            if (uniqueNewMessages.length > 0) {
              console.log(`Adding ${uniqueNewMessages.length} unique new messages`);
              
              // Auto-mark new Slack messages as read if chat page is visible
              if (document.visibilityState === 'visible') {
                const unreadMessages = uniqueNewMessages.filter(m => 
                  m.is_from_slack && !m.is_read
                );
                
                if (unreadMessages.length > 0) {
                  console.log(`Auto-marking ${unreadMessages.length} new messages as read`);
                  for (const message of unreadMessages) {
                    // Don't await these to avoid blocking
                    chatService.markAsRead(message.id).catch(err => 
                      console.error(`Failed to mark message ${message.id} as read:`, err)
                    );
                  }
                }
              }
              
              const combined = [...existingMessages, ...uniqueNewMessages];
              // Sort messages by time
              return combined.sort((a, b) => 
                new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
              );
            }
            
            // If no truly unique messages, return existing state
            return existingMessages;
          });
          
          // Silently update unread count too
          fetchUnreadCount();
        }
      }
    } catch (error) {
      // Just log errors, don't show to user during background checks
      console.error('Silent message fetch failed:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await chatService.getUnreadCount();
      if (response) {
        // Only update if the count is different to avoid unnecessary renders
        if (response.count !== unreadCount) {
          setUnreadCount(response.count);
        }
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      const sentMessage = await chatService.sendMessage(newMessage);
      
      // Add the sent message to the list
      if (sentMessage) {
        setMessages(prev => [...prev, sentMessage]);
        setNewMessage('');
        
        // Show typing indicator after sending a message
        // Simulate a response being typed by the support team
        setTimeout(() => {
          setShowTyping(true);
          scrollToBottom();
          
          // Hide typing indicator after a random interval to simulate real typing
          setTimeout(() => {
            setShowTyping(false);
          }, Math.random() * 1000 + 2000); // Between 2-3 seconds
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleMarkAsRead = async (messageId: number) => {
    try {
      await chatService.markAsRead(messageId);
      
      // Update local message state
      setMessages(prev => 
        prev.map(msg => 
          msg.id === messageId ? { ...msg, is_read: true } : msg
        )
      );
      
      // Update unread count
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!user) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">Please login to access messages</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: isMobile ? 1 : 3, 
      height: isMobile ? 'calc(100vh - 80px)' : 'calc(100vh - 100px)', 
      display: 'flex', 
      flexDirection: 'column',
      maxWidth: isMobile ? '100%' : '90%',
      margin: isMobile ? '0' : '0 auto',
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: isMobile ? 1 : 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            sx={{ 
              bgcolor: blue[500], 
              mr: 1.5,
              width: isMobile ? 28 : 36,
              height: isMobile ? 28 : 36
            }}
          >
            <ChatIcon fontSize={isMobile ? "small" : "medium"} />
          </Avatar>
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"}>
              Messages
              {unreadCount > 0 && (
                <Badge badgeContent={unreadCount} color="error" sx={{ ml: 1 }} />
              )}
            </Typography>
            
            {!isMobile && (
              <Chip
                size="small"
                icon={<RefreshIcon style={{ fontSize: 16, color: green[500] }} />}
                label="Real-time enabled"
                variant="outlined"
                sx={{ 
                  height: 20, 
                  fontSize: '0.65rem',
                  mt: 0.5,
                  bgcolor: green[50],
                  borderColor: green[200],
                  color: green[800]
                }}
                onClick={() => {
                  console.log('Manual refresh requested by user');
                  // First fetch messages immediately 
                  fetchMessages();
                  // Then reconnect WebSocket
                  const token = localStorage.getItem('token');
                  if (token) {
                    websocketService.reconnect(token);
                  }
                }}
              />
            )}
          </Box>
        </Box>
        
        <Box>
          <Tooltip title="Refresh Messages">
            <IconButton 
              onClick={fetchMessages} 
              disabled={loading}
              size={isMobile ? "small" : "medium"}
              sx={{ mr: 0.5 }}
            >
              <RefreshIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Notification Settings">
            <IconButton 
              onClick={() => setShowSettings(!showSettings)}
              size={isMobile ? "small" : "medium"}
              color={showSettings ? "primary" : "default"}
              sx={{ mr: 0.5 }}
            >
              <SettingsIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
          
          {isMobile ? (
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                console.log('Manual refresh requested by user');
                fetchMessages();
                const token = localStorage.getItem('token');
                if (token) {
                  websocketService.reconnect(token);
                }
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          ) : (
            <Tooltip title="Chat with our team for support, questions, or feedback. Messages are delivered directly to our Slack workspace.">
              <IconButton size="small">
                <InfoIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      
      <Collapse in={showSettings} timeout="auto" unmountOnExit>
        <Paper 
          elevation={2} 
          sx={{ 
            mb: 2, 
            borderRadius: isMobile ? 1 : 2,
            position: 'relative',
            zIndex: 10,
            width: '100%'
          }}
        >
          <ChatNotificationSettings />
        </Paper>
      </Collapse>
      
      <Paper 
        elevation={3} 
        sx={{ 
          p: 0, 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          maxHeight: showSettings 
            ? (isMobile ? 'calc(100vh - 300px)' : 'calc(100vh - 350px)') 
            : (isMobile ? 'calc(100vh - 170px)' : 'calc(100vh - 230px)'),
          overflow: 'hidden',
          borderRadius: isMobile ? 1 : 2,
          border: '1px solid',
          borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'divider',
          width: '100%',
          ...(mode === 'dark' && {
            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0))',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          }),
        }}
      >
        {/* Chat header */}
        <Box sx={{ 
          p: isMobile ? 1.5 : 2, 
          backgroundColor: 'primary.main', 
          color: 'primary.contrastText',
          borderTopLeftRadius: isMobile ? 4 : 8,
          borderTopRightRadius: isMobile ? 4 : 8,
        }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Hydralyfe Support (this goes directly to our Slack notifications)
          </Typography>
          <Typography variant="caption">
            Our team typically responds within 1 business day
          </Typography>
        </Box>
        
        {/* Messages area */}
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            flexGrow: 1, 
            backgroundColor: mode === 'dark' ? '#121a24' : '#f5f7f9'
          }}>
            <CircularProgress size={40} thickness={4} />
          </Box>
        ) : (
          <Box 
            ref={messageListRef}
            sx={{ 
              flexGrow: 1, 
              overflow: 'auto', 
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: mode === 'dark' ? '#121a24' : '#f5f7f9'
            }}
          >
            {messages.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                my: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <Avatar 
                  sx={{ 
                    bgcolor: mode === 'dark' ? blue[900] : blue[50], 
                    color: mode === 'dark' ? blue[200] : blue[500],
                    width: 60,
                    height: 60,
                    mb: 2
                  }}
                >
                  <ChatIcon sx={{ fontSize: 30 }} />
                </Avatar>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No messages yet
                </Typography>
                <Typography color="textSecondary" variant="body2" sx={{ maxWidth: 400 }}>
                  Start a conversation with our team. We're here to help with any questions or support you might need.
                </Typography>
              </Box>
            ) : (
              <List sx={{ width: '100%', pt: 1 }}>
                {messages.map((message, index) => {
                  // Check if we need to add a date separator
                  const showDateSeparator = index === 0 || 
                    new Date(message.created_at).toDateString() !== 
                    new Date(messages[index - 1].created_at).toDateString();
                  
                  return (
                    <React.Fragment key={message.id}>
                      {showDateSeparator && (
                        <DateSeparator date={new Date(message.created_at)} />
                      )}
                      <ChatMessage 
                        message={message}
                        onMarkAsRead={handleMarkAsRead}
                      />
                    </React.Fragment>
                  );
                })}
                {showTyping && <TypingIndicator />}
                <div ref={messagesEndRef} />
              </List>
            )}
          </Box>
        )}
        
        {/* Message input area */}
        <Box sx={{ 
          p: isMobile ? 1 : 2, 
          backgroundColor: mode === 'dark' ? '#1a2635' : 'white', 
          borderTop: '1px solid',
          borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'divider'
        }}>
          <form onSubmit={handleSendMessage}>
            <Grid container spacing={isMobile ? 1 : 2} alignItems="flex-end">
              <Grid item xs>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Type your message here..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  disabled={sending}
                  multiline
                  maxRows={4}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      ...(mode === 'dark' && {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.25)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: blue[400],
                        },
                      }),
                    },
                  }}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  endIcon={<SendIcon />}
                  sx={{ 
                    borderRadius: '20px',
                    px: isMobile ? 2 : 3,
                    py: isMobile ? 1 : 1.5,
                    ...(mode === 'dark' && {
                      background: 'linear-gradient(90deg, #4169e1 0%, #5a7ce1 100%)',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #3451b2 0%, #4a6ad1 100%)',
                      },
                    }),
                  }}
                >
                  {sending ? 'Sending...' : 'Send'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChatPage;